import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterMilnerton() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-milnerton",
    name: "Painters Milnerton",
    location: "Milnerton, Cape Town",
    rating: 4.6,
    reviews: 143,
    specialties: ["Coastal Properties", "Residential Estates", "Commercial Buildings", "Wind-Resistant Coatings"],
    image: "https://public.readdy.ai/ai/img_res/02cb299af0dd9491e8452c8a6c2302f8.jpg",
    verified: true,
    yearsExperience: 11,
    website: "https://paintersmilnerton.co.za",
    email: "info@paintersmilnerton.co.za",
    description: "Professional painting contractors serving Milnerton and surrounding coastal areas. We specialize in residential and commercial projects with expertise in coastal protection and wind-resistant finishes.",
    services: [
      "Residential Painting",
      "Commercial Painting", 
      "Coastal Protection Coatings",
      "Estate Painting Services",
      "Wind-Resistant Finishes",
      "Interior Design Painting",
      "Exterior Restoration",
      "Maintenance Programs"
    ],
    serviceAreas: ["Milnerton", "Table View", "Bloubergstrand", "Century City", "Goodwood", "Parow", "Northern Suburbs", "West Coast"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20interior%20living%20room%20painted%20by%20professional%20painters%20in%20Milnerton%20Cape%20Town%2C%20modern%20coastal%20home%20with%20fresh%20walls%2C%20quality%20paint%20finish%2C%20Table%20Mountain%20view%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=milnerton-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20house%20painting%20project%20in%20Milnerton%20Cape%20Town%2C%20residential%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20coastal%20suburb%20setting%2C%20well-maintained%20property&width=400&height=300&seq=milnerton-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Commercial%20painting%20project%20in%20Milnerton%2C%20professional%20painters%20working%20on%20business%20building%2C%20quality%20commercial%20paint%20finish%2C%20modern%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=milnerton-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Estate%20painting%20project%20in%20Milnerton%2C%20professional%20painters%20working%20on%20residential%20complex%2C%20quality%20paint%20finish%2C%20modern%20suburban%20development%2C%20coordinated%20work%20team&width=400&height=300&seq=milnerton-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "David Mitchell",
        rating: 5,
        comment: "Excellent work on our Milnerton home. The team understood the coastal challenges and delivered a finish that has held up beautifully against the strong winds."
      },
      {
        name: "Sarah Botha", 
        rating: 4,
        comment: "Professional service and good quality work. They completed our estate painting project on time and within budget."
      },
      {
        name: "John van der Walt",
        rating: 5,
        comment: "Great painters for coastal properties. Their wind-resistant coatings have performed excellently in our exposed location."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Milnerton | Professional House Painting Services Cape Town | Residential & Commercial";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Milnerton provides professional painting services in Milnerton, Cape Town. Specializing in residential and commercial painting, coastal properties, and quality finishes. Trusted local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Milnerton, house painters Milnerton, painting contractors Cape Town, residential painting, commercial painting services');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20modern%20home%20in%20Milnerton%20Cape%20Town%2C%20coastal%20suburb%20with%20Table%20Mountain%20views%20in%20background%2C%20painters%20in%20clean%20uniforms%20with%20professional%20equipment%2C%20well-maintained%20property%20with%20contemporary%20architecture%2C%20bright%20natural%20lighting%2C%20high%20quality%20residential%20painting%20project&width=1200&height=600&seq=milnerton-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Milnerton - Professional Coastal Painting Contractors</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description} With over 11 years of experience serving the Milnerton community and surrounding coastal areas, we have established ourselves as trusted painting contractors known for our expertise in handling the unique challenges of coastal and wind-exposed properties.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Milnerton Coastal Painting Specialists</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Milnerton's strategic location along the Cape Town coastline, with its proximity to Table Bay and exposure to strong south-easterly winds, creates specific challenges for painting projects. Our team has developed specialized techniques and material selections that address these unique environmental conditions. We understand how the combination of salt air, wind exposure, and temperature variations affects different paint systems, allowing us to recommend and apply the most appropriate solutions for each property's specific exposure and requirements.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Wind-Resistant Coating Technology</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  One of the most significant challenges in Milnerton is the area's exposure to strong coastal winds, particularly the notorious Cape Town south-easter. These winds can cause significant stress on paint films, leading to premature failure if not properly addressed. We use advanced wind-resistant coating systems that provide superior adhesion and flexibility, allowing the paint film to withstand the constant movement and stress caused by wind exposure while maintaining its protective and aesthetic properties.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Residential Estate Painting Excellence</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Milnerton is home to numerous residential estates and developments that require coordinated painting approaches to maintain consistent aesthetic standards and property values. Our estate painting services include comprehensive planning, color coordination, and phased execution that minimizes disruption to residents while ensuring uniform quality across all units. We work closely with estate managers and body corporates to develop maintenance schedules that preserve property values and enhance community appeal.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Commercial and Industrial Painting Solutions</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Milnerton area hosts a diverse range of commercial and light industrial facilities that require specialized painting solutions. Our commercial painting services address the unique requirements of different business types, from office buildings and retail spaces to warehouses and manufacturing facilities. We understand the importance of maintaining professional appearances while minimizing business disruption, and our team is experienced in working flexible schedules to accommodate operational requirements.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Advanced Surface Preparation for Coastal Conditions</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Proper surface preparation is absolutely critical for painting success in coastal environments like Milnerton. Our comprehensive preparation process includes specialized cleaning techniques to remove salt deposits and environmental contaminants, thorough assessment and treatment of any existing damage, and application of appropriate primer systems designed for coastal conditions. We pay particular attention to areas most vulnerable to wind and salt exposure, ensuring that every surface is properly prepared for optimal paint adhesion and longevity.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quality Materials and Environmental Considerations</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We exclusively use premium paint products that are specifically formulated for South African coastal conditions. Our material selection process considers factors such as wind resistance, salt corrosion protection, UV stability, and environmental impact. We maintain relationships with leading paint manufacturers to ensure access to the latest innovations in coating technology, including eco-friendly options that provide excellent performance while minimizing environmental impact on the sensitive coastal ecosystem.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Comprehensive Maintenance Programs</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Understanding that coastal properties require ongoing maintenance to preserve their appearance and protection, we offer comprehensive maintenance programs tailored to Milnerton's specific environmental challenges. These programs include regular inspections, preventive maintenance, and scheduled touch-up services that help extend the life of your paint investment while maintaining optimal appearance and protection year-round.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20residential%20home%20in%20Milnerton%20Cape%20Town%2C%20northern%20suburbs%20coastal%20property%20with%20quality%20paint%20finish%2C%20painters%20in%20uniform%20with%20professional%20equipment%2C%20modern%20suburban%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=milnerton-sidebar-image&orientation=landscape"
                      alt="Professional painters Milnerton residential house painting services Cape Town northern suburbs coastal"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Painting Services in Milnerton</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Coastal Protection Painting Solutions</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our coastal protection painting services in Milnerton are specifically designed to address the unique challenges of wind exposure, salt air, and temperature variations that characterize this coastal suburb. We use advanced coating systems that provide superior protection while maintaining excellent aesthetic appeal throughout the demanding coastal environment.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with wind-resistant materials and expert coastal application techniques.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Residential Estate Services</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our residential estate painting services in Milnerton provide comprehensive solutions for sectional title developments, gated communities, and residential complexes. We understand the importance of maintaining consistent aesthetic standards across all units while working within the constraints of occupied properties. Our estate services include detailed planning, color coordination, and phased execution that ensures minimal disruption to residents.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We work closely with estate managers and body corporates to develop long-term maintenance strategies that preserve property values and enhance community appeal. Our estate painting programs include regular inspections, preventive maintenance, and coordinated repainting schedules that ensure consistent quality and appearance across the entire development.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Commercial Building Solutions</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our commercial painting services in Milnerton cater to the diverse business community, from small retail establishments to large office complexes and light industrial facilities. We understand that commercial properties require painting solutions that maintain professional appearances while withstanding heavy use and environmental exposure. Our commercial services include flexible scheduling to minimize business disruption and specialized coatings designed for high-traffic areas.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    For industrial

                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    For industrial clients, we offer specialized coating services that provide protection against specific environmental challenges, including chemical exposure, heavy wear, and extreme weather conditions. Our industrial painting solutions include epoxy floor coatings, anti-corrosion treatments, and specialized protective finishes designed for manufacturing and processing facilities.
                  </p>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Wind-Resistant Coating Technology</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Milnerton's exposure to strong coastal winds requires specialized coating systems that can withstand constant movement and stress. Our wind-resistant coatings use advanced polymer technologies that provide superior flexibility and adhesion, allowing the paint film to move with the building's natural expansion and contraction while maintaining its protective and aesthetic properties. These specialized systems are particularly important for high-rise buildings and exposed structures that experience significant wind loading.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Painting Projects in Milnerton</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout Milnerton and surrounding areas. Each project demonstrates our commitment to quality workmanship and our expertise in coastal painting applications that withstand wind exposure and environmental challenges.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Milnerton Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional coastal painting with wind-resistant materials and expert application techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Milnerton and surrounding areas have to say about our painting services. Our commitment to excellence and customer satisfaction is reflected in these genuine reviews from homeowners and business owners who have experienced our specialized coastal painting solutions.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Milnerton</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to protect and enhance your Milnerton property with professional coastal painting services? Contact us today for a free consultation and quote. We're here to help you achieve exceptional results that withstand the unique challenges of coastal and wind-exposed environments.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding coastal areas</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Quote Today</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation quote for your Milnerton painting project. Our experienced team will assess your property and provide detailed recommendations tailored to coastal and wind-exposure conditions.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
